import ActivityCard from "@/components/ActivityCard";
import FeaturedActivitySlide from "@/components/FeaturedActivitySlide";
import SingleCarousel from "@/components/SingleCarousel";
import FeaturedActivityOverviewSlide from "@/components/navigator/FeaturedActivityOverviewSlide";
import {
  ResourcePath,
  SavedResourcesAction,
  SavedResourcesToast,
} from "@/components/navigator/SavedResourcesToast";
import withLayout from "@/components/withLayout";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import Analytics from "@/lib/analytics";
import { Activity, OutcomeArea } from "@/lib/models";
import { useEffect, useState } from "react";
import { OutcomeAreaFilterPills } from "@/components/OutcomeAreaFilterPills";
import { ErrorToast } from "@/components/navigator/ErrorToast";

type SavedResourcesToastState = {
  isOpen: boolean;
  currentAction: SavedResourcesAction | null;
};

const ActivityLibraryPage = () => {
  const [savedResourcesToastState, setSavedResourcesToastState] =
    useState<SavedResourcesToastState>({
      isOpen: false,
      currentAction: null,
    });

  const [isErrorToastOpen, setIsErrorToastOpen] = useState<boolean>(false);
  const { activities, outcomeAreas, getActivitiesForCompetency } =
    useEntities();

  const [displayedActivities, setDisplayedActivities] =
    useState<Activity[]>(activities);

  const [filteredOutcomeAreas, setFilteredOutcomeAreas] = useState<
    OutcomeArea[]
  >([]);

  const addToList = (outcomeArea: OutcomeArea) => {
    setFilteredOutcomeAreas((areas) => [...areas, outcomeArea]);
  };

  const removeFromList = (outcomeArea: OutcomeArea) => {
    setFilteredOutcomeAreas((areas) =>
      areas.filter((oA) => oA !== outcomeArea),
    );
  };

  const featuredActivities = activities
    .filter((activity) => {
      return !!activity.featured;
    })
    .sort((activity1, activity2) => {
      if (activity1.featuredOrder && activity2.featuredOrder) {
        return activity1.featuredOrder - activity2.featuredOrder;
      }
      return 0;
    });

  const headingString = `Activities (${displayedActivities.length})`;

  const featuredActivitySlides = featuredActivities.map((activity) => {
    return <FeaturedActivitySlide activity={activity} key={activity.name} />;
  });
  featuredActivitySlides.unshift(
    <FeaturedActivityOverviewSlide key="activity-overview-slide" />,
  );

  const handleAddToSavedResources = (activity: Activity) => () => {
    setSavedResourcesToastState({
      isOpen: true,
      currentAction: SavedResourcesAction.Add,
    });
    Analytics.handleAddActivityToSavedResourcesEvent(
      activity.urlSlug,
      activity.name,
    );
  };
  const handleRemoveFromSavedResources = (activity: Activity) => () => {
    setSavedResourcesToastState({
      isOpen: true,
      currentAction: SavedResourcesAction.Remove,
    });
    Analytics.handleRemoveActivityFromSavedResourcesEvent(
      activity.urlSlug,
      activity.name,
    );
  };

  const handleUpdateSavedResourcesError = () => {
    setIsErrorToastOpen(true);
  };
  const handleCloseToast = () => {
    setSavedResourcesToastState((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (filteredOutcomeAreas.length == 0) {
      setDisplayedActivities(activities);
    } else {
      const filteredActivities = filteredOutcomeAreas.flatMap((outcomeArea) => {
        return outcomeArea.competencies.flatMap((competencyId) => {
          return getActivitiesForCompetency(competencyId);
        });
      });
      setDisplayedActivities(filteredActivities);
    }
  }, [activities, filteredOutcomeAreas, getActivitiesForCompetency]);

  return (
    <div className="min-h-[calc(100vh-72px)] ">
      <div className="mx-4 mb-8 grid max-w-screen-large grid-cols-4 gap-x-4 gap-y-8 small:grid-cols-8 medium:mx-6 medium:mb-10 medium:grid-cols-12 medium:gap-y-10 large:mx-auto large:gap-x-6">
        <SingleCarousel slides={featuredActivitySlides} />

        <div className="col-span-full text-center font-roobert text-2xl font-medium leading-tight text-dark-color">
          {"Select an area of learning and we'll suggest relevant activities."}
        </div>

        <div className="col-span-full -mx-4 overflow-x-scroll medium:mx-0">
          <OutcomeAreaFilterPills
            outcomeAreas={outcomeAreas}
            includeInFilter={addToList}
            excludeFromFilter={removeFromList}
          />
        </div>

        {!!displayedActivities.length ? (
          <div className="col-span-full grid grid-cols-subgrid">
            <h1 className="col-span-full mb-6 font-roobert text-2xl font-medium leading-tight text-neutral-800">
              {headingString}
            </h1>
            <div className="col-span-full grid grid-cols-subgrid gap-x-4 gap-y-4 medium:gap-y-6 large:gap-x-6">
              {displayedActivities.map((activity) => (
                <ActivityCard
                  key={activity.urlSlug}
                  activity={activity}
                  className="col-span-4 large:col-span-3"
                  onAddToSavedResources={handleAddToSavedResources(activity)}
                  onRemoveFromSavedResources={handleRemoveFromSavedResources(
                    activity,
                  )}
                  onUpdateSavedResourcesError={handleUpdateSavedResourcesError}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="col-span-full mb-[100px]">
            <div className="mb-5 text-center font-roobert text-4xl font-medium leading-tight text-dark-color">
              No activities found
            </div>
            <div className="text-center font-roobert text-xl font-medium leading-tight text-gray-text">
              Sorry, that filter has no results.
            </div>
          </div>
        )}
      </div>
      <SavedResourcesToast
        isOpen={savedResourcesToastState.isOpen}
        currentAction={savedResourcesToastState.currentAction}
        resourcePath={ResourcePath.Activity}
        handleClose={handleCloseToast}
      />
      <ErrorToast
        isOpen={isErrorToastOpen}
        handleClose={() => setIsErrorToastOpen(false)}
      />
    </div>
  );
};

export default withLayout(ActivityLibraryPage);
